"use client";
import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTranslation } from "react-i18next";

// Order matches the design: 23 / 12 / 8 / 5 hours
const stats = [
  { key: "stat1" },
  { key: "stat4" },
  { key: "stat2" },
  { key: "stat3" },
];

const RecruiterDrowningSection = () => {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ py: { xs: 4, sm: 5, md: 6 }, backgroundColor: "#fff" }}>
      <Container maxWidth="lg">
        {/* Top Section: Heading (left) + Stats list (right) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 5 },
            alignItems: "start",
            mb: { xs: 3, sm: 3.5, md: 4 },
            mt:'1%'
          }}
        >
          {/* Left: Kicker + Heading + Total Banner */}
          <Box>
            {/* Kicker */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 700,
                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.72rem" },
                color: "#E2622F",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              {t("drowningSection.description")}
            </Typography>

            {/* Heading */}
            <Typography
              component="h2"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: { xs: "1.3rem", sm: "1.6rem", md: "2.2rem" },
                color: "#14122B",
                lineHeight: 1.3,
                mb: { xs: 2.5, sm: 3, md: 3.5 },
                maxWidth: { md: "500px" },
              }}
            >
              {t("drowningSection.title")}{" "}
              <Box component="span" sx={{ color: "#FD5D02" }}>
                {t("drowningSection.titleHighlight")}
              </Box>{" "}
              {t("drowningSection.titleSuffix")}
            </Typography>

            {/* Total Banner */}
            <Box
              sx={{
                backgroundImage:
                  "url('https://getgeek-website-assets.b-cdn.net/image%20(5).webp')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                borderRadius: "16px",
                py: { xs: 2, sm: 2.5, md: 2.8 },
                px: { xs: 2, sm: 2.5, md: 3 },
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                maxWidth: { md: "420px" },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "16px",
                  zIndex: 0,
                },
                "& > *": {
                  position: "relative",
                  zIndex: 1,
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 700,
                  fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2rem" },
                  color: "#fff",
                }}
              >
                {t("drowningSection.total.hours")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.75rem", sm: "0.82rem", md: "0.85rem" },
                  color: "rgba(255,255,255,0.9)",
                  mt: 0.3,
                }}
              >
                {t("drowningSection.total.label")}
              </Typography>
            </Box>
          </Box>

          {/* Right: Stats List Card */}
          <Box
            sx={{
              border: "1px solid #ffd7d7",
              borderRadius: "16px",
              overflow: "hidden",
              background: "#fff",
              mt: { xs: 2, sm: '5%', md: '12%',lg:'6%' }
            }}
          >
            {stats.map(({ key }, index) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2.5 },
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1.6, sm: 1.9 },
                  backgroundColor: index % 2 === 0 ? "#FCEBEB" : "#fff",
                  borderBottom:
                    index < stats.length - 1 ? "1px solid #ffd7d7" : "none",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.4rem" },
                    color: "#fd4d1a",
                    whiteSpace: "nowrap",
                    minWidth: { sm: "80px" },
                  }}
                >
                  {t(`drowningSection.${key}.hours`)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.72rem", sm: "0.78rem", md: "0.8rem" },
                    color: "#55556E",
                    lineHeight: 1.4,
                    fontWeight: 400,
                  }}
                >
                  {t(`drowningSection.${key}.label`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer Text - 3 columns */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: { xs: 2, sm: 3, md: 4 },
            textAlign: "left",
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.78rem", sm: "0.85rem", md: "1.1rem" },
              color: "#14122B",
              lineHeight: 1.8,
            }}
          >
            {t("drowningSection.footertextOne")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.9rem" },
              color: "#55556E",
              lineHeight: 1.8,
            }}
          >
            {t("drowningSection.footertextTwo")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.9rem" },
              color: "#55556E",
              lineHeight: 1.8,
            }}
          >
            {t("drowningSection.footertextThree")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RecruiterDrowningSection;